import React, {useCallback, useEffect, useState} from "react";
import styled from "styled-components";
import axios from "axios";
import FairInfoBar from "../FairInfoBar";
import { useNavigate } from "react-router-dom";
import { RedButton } from "../Button";
import Heading from "../Heading";
import { handleGenericErrors, handleGenericSuccess } from "../../utils/ErrorUtility";
import LeftRail from "./LeftRail/LeftRail";
import RadioButton from "../RadioButton";
import { convertToCAD, convertToUSD } from "../../utils/CurrencyConversion";
import SaveChangesModal from "./SaveChangesModal";
import CurrencyInput from "../CurrencyInput";
import { StyledRedButton, StyledWhiteButton } from "./FinFormSalesSummaryPage";
import { trackFinFormApplyButton, trackFinFormBackButton, trackFinFormEarningsSelection, trackFinFormEditButton, trackFinFormPageChange, trackFinFormSaveAndContinueButton, trackFinFormTotalProductOff, updateUserFairId } from "../../DumbleData";

interface Props {
	language: string;
}

export const FinFormRewardsPage = ( {language} : Props ) => {
    const [fairInfo, setFairInfo] = useState<any>();
    const [latestStep, setLatestStep] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [netSales, setNetSales] = useState<string>("0.00");
    const [changesMade, setChangesMade] = useState<boolean>(false);
    const [nextLocation, setNextLocation] = useState("/financial-form/review")
    const [saveChangesModalOpen, setSaveChangesModalOpen] = useState<boolean>(false);
    const [isBogoFair, setIsBogoFair] = useState<boolean>(false);
    const bogoMinThreshold = isBogoFair && Number(netSales) >= 1500;

    // Bonus Summary
    const [secondFairRate, setSecondFairRate] = useState<string>("0.00");
    const [secondFairBonus, setSecondFairBonus] = useState<string>("0.00");
    const [specialRate, setSpecialRate] = useState<string>("0.00");
    const [specialBonus, setSpecialBonus] = useState<string>("0.00");
    const [total, setTotal] = useState<string>("0.00");

    // Earnings
    const [productRate, setProductRate] = useState<string>("0.00");
    const [cashEligible, setCashEligible] =useState<boolean>(true);
    const [maxProduct, setMaxProduct] = useState<string>("0.00");
    const [scholasticDollarsRate, setScholasticDollarsRate] = useState<string>("0.00");
    const [maxScholasticDollars, setMaxScholasticDollars] = useState<string>("0.00");
    const [cashRate, setCashRate] = useState<string>("0.00");
    const [maxCash, setMaxCash] = useState<string>("0.00");

    // Split
    const [product, setProduct] = useState<string>("0.00");
    const [previousProduct, setPreviousProduct] = useState<string>("0.00");
    const [eligibleProductRemaining, setEligibleProductRemaining] = useState<string>("0.00");
    const [appliedDisabled, setAppliedDisabled] = useState<boolean>(false);
    const [applied, setApplied] = useState<boolean>(false);
    const [productToSDConversionFactor, setProductToSDConversionFactor] = useState<string>("0.00");
    const [splitMaxScholasticDollars, setSplitMaxScholasticDollars] = useState<string>("0.00");
    const [scholasticDollars, setScholasticDollars] = useState<string>("0.00");
    const [scholasticDollarsError, setScholasticDollarsError] = useState<string>();
    const [productToCashConversionFactor, setProductToCashConversionFactor] = useState<string>("0.00");
    const [splitMaxCash, setSplitMaxCash] = useState<string>("0.00");
    const [cash, setCash] = useState<string>("0.00");
    const [cashError, setCashError] = useState<string>();

    const [openAddProductCard, setOpenAddProductCard] = useState<boolean>(false);
    const [productTakenSelected, setProductTakenSelected] = useState<string>("0.00");
    const [originalAdditionalProduct, setOriginalAdditionalProduct] = useState<string>("0.00");
    const [isAdditionalProductDisabled, setIsAdditionalProductDisabled] = useState<boolean>(false);

    const [selected, setSelected] = useState<string>("PRODUCT");
    const [scholasticDollarsBalance, setScholasticDollarsBalance] = useState<string>("0.00");
    const navigate = useNavigate();

    const formatCurrency = (currency: String) => {
        let output = currency.replace(/\D/g, "");
        output = output.replace(/^0+(?=\d)/, "");

        // accounting for ctrl + a behavior
        if (output.length === 2) {
            output = "0" + output;
        } else if (output.length === 1) {
            output = "00" + output;
        } else if (output.length === 0) {
            output = "000";
        }

        output = output.substring(0,output.length-2) + "." + output.substring(output.length-2);
        setChangesMade(true);
        return output;
    };
    const handleProduct = async (currency: string) => {
        const currNum = formatCurrency(currency);
        // checking max
        if(parseFloat(currNum) < parseFloat(maxProduct)) {
            setProduct(currNum);
        } else {
            setProduct(maxProduct);
        }
        // checking min
        if(parseFloat(currNum) < parseFloat(total)) {
            setAppliedDisabled(true);
        } else {
            setAppliedDisabled(false);
        }
    }
    const handleScholasticDollars = async (currency: String) => {
        if( Number(formatCurrency(currency)) >= Number(splitMaxScholasticDollars) ) {
            setScholasticDollars(formatCurrency(splitMaxScholasticDollars));
            setCash("0.00");
        } else {
            const cashTaken = (Number(eligibleProductRemaining) - (Number(formatCurrency(currency)) / Number(productToSDConversionFactor))) * Number(productToCashConversionFactor);
            setCash(cashTaken.toFixed(2));
            setScholasticDollars(formatCurrency(currency))
        }
    }
    const handleCash = async (currency: String) => {
        if ( Number(formatCurrency(currency)) >= Number(splitMaxCash) ) {
            setCash(formatCurrency(splitMaxCash));
            setScholasticDollars("0.00");
        } else {
            const scholasticDollarsTaken = (Number(eligibleProductRemaining) - (Number(formatCurrency(currency)) / Number(productToCashConversionFactor))) * Number(productToSDConversionFactor);
            setScholasticDollars(scholasticDollarsTaken.toFixed(2));
            setCash(formatCurrency(currency));
        }
    }

    const handleBack = async () => {
        trackFinFormBackButton();
        if (openAddProductCard) {
            setOpenAddProductCard(false);
            setIsAdditionalProductDisabled(false);
            setProductTakenSelected(originalAdditionalProduct);
        }
        else {
            navigate("/financial-form/sales-summary");
        }
    }

    const handleApply = async () => {
        if (applied) {
            trackFinFormEditButton();
            setApplied(false);
            setScholasticDollars("0.00");
            setCash("0.00");
        }
        else {
            trackFinFormApplyButton();
            const remainingProduct = Number(maxProduct) - Number(product);
            setEligibleProductRemaining(remainingProduct.toString());
            setSplitMaxScholasticDollars((remainingProduct * Number(productToSDConversionFactor)).toFixed(2).toString());
            setSplitMaxCash((remainingProduct * Number(productToCashConversionFactor)).toFixed(2).toString());
            setApplied(true);
            setScholasticDollarsError(undefined);
            setCashError(undefined);

            if (product !== previousProduct) {
                setPreviousProduct(product);
                setScholasticDollars("0.00");
                setCash("0.00");
            }

            if (Number(cashRate) === 0) {
                setScholasticDollars((remainingProduct * Number(productToSDConversionFactor)).toFixed(2).toString());
            }
        }
    }

    const dataValidate = useCallback((language: string) => {
        let isAllValid = true;
        if (formatCurrency(scholasticDollars) === "0.00" && formatCurrency(cash) === "0.00" && (Number(product) !== Number(maxProduct))) {
            setScholasticDollarsError(language === "en" ? "Please Choose an Amount" : "Veuillez choisir un montant");
            setCashError(language === "en" ? "Please Choose an Amount" : "Veuillez choisir un montant")
            isAllValid = false;
        }
        else {
            setScholasticDollarsError(undefined);
            setCashError(undefined);
        }
        return isAllValid;
    }, [product, scholasticDollars, cash, maxProduct]);

    const handleRewards = async () => {
        trackFinFormSaveAndContinueButton();
        if(!openAddProductCard && Number(scholasticDollarsBalance) !== 0 && (selected === "PRODUCT" || isBogoFair)){
            window.scrollTo({
				top: 0,
				left: 0,
				behavior: 'smooth'
			});
            setOpenAddProductCard(true);
            return;
        }
        trackFinFormEarningsSelection(selected);
        putRewards();
    }

    const minTotal = parseFloat(maxProduct);
    const maxTotal = parseFloat(maxProduct) + parseFloat(scholasticDollarsBalance);
    // + 0 prevents this from displaying as -$0.00 in edge cases w/ floating point arithmetic
    const remainingTotal = parseFloat(productTakenSelected) - minTotal + 0;

    const handleProductTakenChange = (currency: String) => {
        // Validate Minimum
        if (Number(formatCurrency(currency)) < minTotal) {
            setIsAdditionalProductDisabled(true);
        } else {
            setIsAdditionalProductDisabled(false);
        }

        // Validate Maximum
        if (Number(formatCurrency(currency)) > maxTotal) {
            setProductTakenSelected(formatCurrency(maxTotal.toFixed(2)));
        }
        else {
            setProductTakenSelected(formatCurrency(currency));
        }
    };

    // GET Request
    useEffect(() => {
        const getFinancialFormRewards = async () => {
            try {
                await axios.get(
                    `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/rewards`,
                        {
                            withCredentials: true,
                        }
                )
                .then((response) => {
                    if (response.status === 200) {
                        if (!response.data.isFinancialFormAvailable) {
                            navigate("/financial-form/intro");
                        };
                        if (response.data.latestStep === "CLOSED") {
                            navigate("/financial-form/confirmation");
                        }
                        if (response.data.latestStep === "SALES" || response.data.latestStep === "SALES_SUMMARY") {
                            navigate("/financial-form/intro");
                        }
                        updateUserFairId(response.data.fair.id);

                        const fairInfo = response.data.fair;
                        fairInfo.start = response.data.fair.startDate;
                        fairInfo.end = response.data.fair.endDate;
                        setFairInfo(fairInfo);
                        setLatestStep(response.data.latestStep);

                        setNetSales(response.data.salesSummary.netSales.toFixed(2));
                        setScholasticDollarsBalance(response.data.scholasticDollarBalance);
                        setIsBogoFair(response.data.isBogoFair);

                        // Bonus Summary
                        const bonusSummary = response.data.bonusSummary;
                        setSecondFairRate((bonusSummary.secondFairProductRate * 100).toString());
                        setSecondFairBonus(bonusSummary.secondFairProductBonus.toFixed(2));
                        setSpecialRate((bonusSummary.specialRate * 100).toString());
                        setSpecialBonus(bonusSummary.specialBonus.toFixed(2));
                        setTotal(bonusSummary.total.toFixed(2));
                        if(bonusSummary.total > 0) {
                            setProduct(bonusSummary.total.toFixed(2));
                            setPreviousProduct(bonusSummary.total.toFixed(2));
                        }

                        // Earnings
                        const earnings = response.data.earnings;
                        setCashEligible(earnings.cashEligible);
                        setProductRate((earnings.productRate * 100).toString());
                        setMaxProduct(earnings.maxProductAmount.toFixed(2));
                        setScholasticDollarsRate((earnings.scholasticDollarsRate * 100).toString());
                        setMaxScholasticDollars(earnings.maxScholasticDollars.toFixed(2));
                        setCashRate((earnings.cashRate * 100).toString());
                        setMaxCash(earnings.maxCash.toFixed(2));

                        // Earnings Previous User Inputs
                        setSelected(earnings.selected);

                        if (earnings.selectedProductAmount < bonusSummary.total || earnings.selectedProductAmount > earnings.maxProductAmount) {
                            setProduct(0.00.toFixed(2));
                            setPreviousProduct(0.00.toFixed(2));
                            setAppliedDisabled(true);
                        }
                        else {
                            setProduct(earnings.selectedProductAmount.toFixed(2));
                            setPreviousProduct(earnings.selectedProductAmount.toFixed(2));
                        }
                        setScholasticDollars(earnings.selectedScholasticDollars.toFixed(2));
                        setCash(earnings.selectedCash.toFixed(2));
                        const grandTotalProduct = earnings.maxProductAmount + earnings.additionalProductTaken;
                        setOriginalAdditionalProduct(grandTotalProduct.toFixed(2));
                        setProductTakenSelected(grandTotalProduct.toFixed(2));

                        // Split
                        setProductToSDConversionFactor((earnings.scholasticDollarsRate / earnings.productRate).toString());
                        setProductToCashConversionFactor((earnings.cashRate / earnings.productRate).toString());
                    }
                    else {
                        handleGenericSuccess(response);
                    }
                })
                .catch((err) => {
                    handleGenericErrors(err);
                });
            } catch (err: any) {
                handleGenericErrors(err);
            } finally {
                setIsLoading(false);
                trackFinFormPageChange("Financial Form Rewards Page");
            }
        };
        getFinancialFormRewards();
    }, [navigate]);

    // Put Request
    const putRewards = async () => {
        try {
            const rewards = getRequestBody();
            if(rewards === null){ return; }
            await axios.put(
                `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/rewards`,
                rewards,
                {
                    withCredentials: true,
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    // Navigate to Review
                    navigate(nextLocation);
                }
                else {
                    handleGenericSuccess(response);
                }
            })
            .catch((err) => {
                handleGenericErrors(err);
            });
        } catch (err: any) {
            handleGenericErrors(err);
        }
    };

    const getRequestBody = () => {
        const requestBody = {
            selected: selected,
            product: "0.00",
            totalProduct: "0.00",
            scholasticDollars: "0.00",
            cash: "0.00",
        }
        if(isBogoFair){
            (Number(minTotal) === Number(productTakenSelected)) ? trackFinFormTotalProductOff(false) : trackFinFormTotalProductOff(true);
            requestBody.selected = "PRODUCT";
            requestBody.product = maxProduct;
            requestBody.totalProduct = productTakenSelected;
            return requestBody;
        }
        if(selected === "PRODUCT"){
            (Number(minTotal) === Number(productTakenSelected)) ? trackFinFormTotalProductOff(false) : trackFinFormTotalProductOff(true);
            if (Number(scholasticDollarsBalance) === 0) {
                requestBody.product = maxProduct;
                requestBody.totalProduct = maxProduct;
                return requestBody;
            }
            const requestBodytotalProduct = {
                selected: selected,
                product: (minTotal.toFixed(2)),
                scholasticDollars: 0,
                cash: 0,
                totalProduct: productTakenSelected,
            }
            return requestBodytotalProduct;
        }
        if(selected === "SCHOLASTIC_DOLLARS"){
            requestBody.scholasticDollars = maxScholasticDollars;
            return requestBody;
        }
        if(selected === "CASH"){
            requestBody.cash = maxCash;
            return requestBody;
        }
        if(selected === "SPLIT"){
            if(dataValidate(language)){
                requestBody.product = product;
                requestBody.totalProduct = product;
                requestBody.scholasticDollars = scholasticDollars;
                requestBody.cash = cash;
                return requestBody;
            }
            else {
                saveChangesModalOpen && setSaveChangesModalOpen(false);
                return null;
            }
        }
        return requestBody;
    }

    // tracking if changes have been made
    useEffect(() => {
        if(!isLoading) {
            setChangesMade(true);
        }
    }, [selected]);

    // handling left rail clicks
    const handleNextLocation = (leftRailLocation: string) => {
        if(changesMade) {
            setNextLocation(leftRailLocation);
            setSaveChangesModalOpen(true);
        } else {
            navigate(leftRailLocation);
        }
    }

    // updating nextLocation when save changes modal closed
    useEffect(() => {
        if(!saveChangesModalOpen) {
            setNextLocation("/financial-form/review");
        }
    }, [saveChangesModalOpen])

    const labelProduct = () => {
        if (Number(secondFairBonus) !== 0 && Number(specialBonus) !== 0) {
            return language === "en"
                ? `(Minimum: $${convertToUSD(total)} bonus total`
                : `(Minimum : ${convertToCAD(total)} $ total de la prime`;
        } else if (Number(secondFairBonus) === 0 && Number(specialBonus) !== 0) {
            return language === "en"
                ? `(Minimum: $${convertToUSD(specialBonus)} Special bonus`
                : `(Minimum : ${convertToCAD(specialBonus)} $ Special bonus`;
        } else if (Number(secondFairBonus) !== 0 && Number(specialBonus) === 0) {
            return language === "en"
                ? `(Minimum: $${convertToUSD(secondFairBonus)} 2nd Fair bonus`
                : `(Minimum : ${convertToCAD(secondFairBonus)} $ 2nd Fair bonus`;
        } else {
            return language === "en"
                ? `(Maximum: $${convertToUSD(maxProduct)})`
                : `(Maximum : ${convertToCAD(maxProduct)} $)`;
        }
    };

    const sublabelProduct = () => {
        if (Number(secondFairBonus) === 0 && Number(specialBonus) === 0) {
            return undefined;
        } else {
            return language === "en"
                ? `Maximum: $${convertToUSD(maxProduct)})`
                : `Maximum : ${convertToCAD(maxProduct)} $)`;
        }
    };

    const labelProductRadioButton = () => {
        if (language === "en") {
            if (Number(total) > 0) {
                return "$" + convertToUSD(total) + " + $" + convertToUSD((Number(maxProduct)-Number(total))) + " in product taken off the Fair";
            }
            else {
                return "$" + convertToUSD(maxProduct) + " in product taken off the Fair";
            }
        }
        else {
            if (Number(total) > 0) {
                return convertToCAD(total) + " $ + " + convertToCAD((Number(maxProduct)-Number(total))) + " $" + " en produits pris au Festival";
            }
            else {
                return convertToCAD(maxProduct) + " $" + " en produits pris au Festival";
            }
        }
    }

    const sublabelProductRadioButton = () => {
        if (language === "en") {
            if ( Number(total) > 0 ) {
                return "(Bonus Total + Net Sales x " + productRate + "%)";
            } else {
                return "(Net Sales x " + productRate + "%)";    
            }
        } else {
            if (Number(total) > 0 ) {
                return "(total de la prime + ventes nettes x " + productRate + " %)";
            } else {
                return "(ventes nettes x " + productRate + " %)";
            }
        }
    }

    const sublabelSplitRadioButton = () => {
        if (language === "en") {
            if (cashEligible) {
                return "Split between product, Scholastic Dollars, and cash";
            } else {
                return "Split between product and Scholastic Dollars";
            }
        } else {
            if (cashEligible) {
                return "Diviser les récompenses en produits, en dollars Scholastic et en argent comptant";
            } else {
                return "Diviser les récompenses en produits et en dollars Scholastic";
            }
        }
    }

    return (<>
        {!isLoading && (<>
            <FairInfoBar
                language={language}
                enableSwitch={fairInfo.enableSwitch}
                fairInfoData={fairInfo}
                organizationData={fairInfo}
                pageType={"financial-form"}
                />
            <LeftRail
                language={language}
                latestStep={latestStep}
                handleNextLocation={handleNextLocation}
            />
            <StyledDiv>
                <StyledHeading>{language === "en" ? "Rewards" : "Récompenses"}</StyledHeading>
                {!openAddProductCard && (
                <>
                {(bogoMinThreshold || !isBogoFair) && (                
                    <StyledCard>
                        <SubHeading>{language === "en" ? "Sales Summary" : "Sommaire des ventes"}</SubHeading>
                        <NetSales>
                                <p>{language === "en" ? "Net Sales" : "Ventes nettes"}</p>
                                <p>{language === "en" ? "$" + convertToUSD(netSales) : convertToCAD(netSales) + " $"}</p>
                        </NetSales>
                    </StyledCard>)}
                {isBogoFair ? (
                    <>
                        {(!bogoMinThreshold) ? (
                            <StyledCard>
                                <StyledSoloSubHeading>{language === "en" ? `Your total Net Sales did not reach the minimum threshold to receive product rewards.` : `Le total de vos ventes nettes n’a pas atteint le seuil minimal pour recevoir des récompenses en produits.`}</StyledSoloSubHeading>
                            </StyledCard>
                        ) : (
                            <StyledCard>
                                <StyledInnerCard>
                                    <StyledSpacedSubHeading>{language === "en" ? `Your earnings` : `Vos gains`}</StyledSpacedSubHeading>
                                    <div>
                                        <StyledWrappedSubLabel>{language === "en" ? `$${convertToUSD(maxProduct)} in Product Taken off the Fair` : `${convertToCAD(maxProduct)} $ en produits pris au Festival`}</StyledWrappedSubLabel>
                                        <StyledWrappedSubLabel>{language === "en" ? `(Net Sales x ${productRate}%)` : `(ventes nettes x ${productRate} %)`}</StyledWrappedSubLabel>
                                    </div>
                                    <DisclaimerText>{language === "en" ? `Product rewards MUST be taken off the Fair at full price.` : `Les récompenses en produits DOIVENT être prises parmi les articles à prix régulier lors du Festival en cours.`}</DisclaimerText>
                                </StyledInnerCard>
                            </StyledCard>
                        )}
                    </>
                ) : (
                <>
                {/* Bonus Summary Section */}
                {(Number(secondFairBonus) !== 0 || Number(specialBonus) !== 0) && <StyledCard>
                    <SubHeading>{language === "en" ? "Bonus Summary" : "Sommaire des primes"}</SubHeading>
                    {Number(secondFairBonus) !== 0 && <StyledRow>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "2nd Fair Product Bonus" : "Prime pour un deuxième Festival"}</StyledSubLabel>
                            {language === "en" ? "(Must be taken as product off the current fair: Net Sales x " + secondFairRate + "%)" : "(doit être prise en produits au Festival en cours : ventes nettes x " + secondFairRate + " %)"}
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? "$" + convertToUSD(secondFairBonus) : convertToCAD(secondFairBonus) + " $"}</StyledNumber>
                    </StyledRow>}
                    {Number(specialBonus) !== 0 && <StyledRow className={Number(secondFairBonus) !== 0 && Number(specialBonus) !== 0 ? "bothBonuses" : ""}>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Special Bonus" : "Prime spéciale"}</StyledSubLabel>
                            {language === "en" ? "(Must be taken as product off the current fair: Net Sales x " + specialRate + "%)": "(doit être prise en produits au Festival en cours : ventes nettes x " + specialRate + " %)"}
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? "$" + convertToUSD(specialBonus) : convertToCAD(specialBonus) + " $"}</StyledNumber>
                    </StyledRow>}
                    {Number(secondFairBonus) !== 0 && Number(specialBonus) !== 0 && <StyledRow className="bothBonuses">
                        <p>{language === "en" ? "Total" : "Total"}</p>
                        <p>{language === "en" ? "$" + convertToUSD(total) : convertToCAD(total) + " $"}</p>
                    </StyledRow>}
                </StyledCard>}

                <StyledCard>
                    <SubHeading>{language === "en" ? "Choose how you would like to receive your earnings:" : "Choisissez comment vous souhaitez recevoir vos gains :"}</SubHeading>
                    <StyledRadioButton
                        checked={selected === "PRODUCT"}
                        onChange={() => {setSelected("PRODUCT")}}
                        label={labelProductRadioButton()}
                        label2={sublabelProductRadioButton()}
                        radioButtonName="PRODUCT"
                        tabIndex={0}
                    ></StyledRadioButton>
                    {Number(scholasticDollarsRate) !== 0 && Number(secondFairBonus) === 0 && Number(specialBonus) === 0 && <StyledRadioButton
                        checked={selected === "SCHOLASTIC_DOLLARS"}
                        onChange={() => {setSelected("SCHOLASTIC_DOLLARS")}}
                        label={language === "en" ? "$" + convertToUSD(maxScholasticDollars) + " in Scholastic Dollars" : convertToCAD(maxScholasticDollars) + " $" + " en dollars Scholastic"}
                        label2={language === "en" ? "(Net Sales x " + scholasticDollarsRate + "%)" : "(ventes nettes x " + scholasticDollarsRate + " %)"}
                        radioButtonName="SCHOLASTIC_DOLLARS"
                        tabIndex={0}
                    ></StyledRadioButton>}
                    {Number(cashRate) !== 0 && Number(secondFairBonus) === 0 && Number(specialBonus) === 0 && <StyledRadioButton
                        checked={selected === "CASH"}
                        onChange={() => {setSelected("CASH")}}
                        label={language === "en" ? "$" + convertToUSD(maxCash) + " in cash" : convertToCAD(maxCash) + " $" + " en argent comptant"}
                        label2={language === "en" ? "(Net Sales x " + cashRate + "%)" : "(ventes nettes x " + cashRate + " %)"}
                        radioButtonName="CASH"
                        tabIndex={0}
                    ></StyledRadioButton>}

                    {/* Split Section */}
                    <StyledRadioButton
                        checked={selected === "SPLIT"}
                        onChange={() => {setSelected("SPLIT")}}
                        label={sublabelSplitRadioButton()}
                        radioButtonName="SPLIT"
                        tabIndex={0}
                    ></StyledRadioButton>
                    {selected === "SPLIT" && <>
                        <StyledTextInput
                        heading={language === "en" ? "Product taken off the Fair" : "Produits pris au Festival"}
                        value={product}
                        language={language}
                        onChange={handleProduct}
                        label={labelProduct()}
                        sublabel={sublabelProduct()}
                        disabled={applied}
                        ></StyledTextInput>
                        <ApplyButton 
                            handleClick={handleApply} 
                            className={applied ? "editAmount" : ""}
                            disabled={appliedDisabled}>
                            {language === "en" && applied ? "Edit Amount" : ""}
                            {language === "en" && !applied ? "Apply" : ""}
                            {language === "fr" && applied ? "Modifier le montant" : ""}
                            {language === "fr" && !applied ? "Appliquer" : ""}
                        </ApplyButton>
                        {applied && Number(cashRate) === 0 && <StyledTextInput
                        heading={language === "en" ? "Scholastic Dollars" : "Dollars Scholastic"}
                        value={scholasticDollars}
                        language={language}
                        onChange={handleScholasticDollars}
                        label={`${language === "en" ? "(Maximum: $" + convertToUSD(splitMaxScholasticDollars) : "(Maximum : " + convertToCAD(splitMaxScholasticDollars) + " $"})`}
                        error={scholasticDollarsError}
                        disabled={true}
                        ></StyledTextInput>}
                        {applied && Number(cashRate) !== 0 && <StyledTextInput
                        heading={language === "en" ? "Scholastic Dollars" : "Dollars Scholastic"}
                        value={scholasticDollars}
                        language={language}
                        onChange={handleScholasticDollars}
                        label={`${language === "en" ? "(Maximum: $" + convertToUSD(splitMaxScholasticDollars) : "(Maximum : " + convertToCAD(splitMaxScholasticDollars) + "  $"})`}
                        error={scholasticDollarsError}
                        ></StyledTextInput>}
                        {applied && Number(cashRate) !== 0 && <StyledTextInput
                        heading={language === "en" ? "Cash" : "Argent comptant"}
                        value={cash}
                        language={language}
                        onChange={handleCash}
                        label={`${language === "en" ? "(Maximum: $" + convertToUSD(splitMaxCash) : "(Maximum : " + convertToCAD(splitMaxCash) + " $"})`}
                        error={cashError}
                        ></StyledTextInput>}
                    </>}
                </StyledCard>
                </>)}
                </>)}

                {/* Additional Product */}
                {openAddProductCard && (                
                    <StyledCard>
                        <StyledSubHeading>{language === "en" ? "You have $" + convertToUSD(scholasticDollarsBalance) + " in existing Scholastic Dollars Rewards. Would you like to take additional product off the Fair?" : "Vous avez " +  convertToCAD(maxScholasticDollars) + " $ en dollars Scholastic existants. Souhaitez-vous prendre un produit supplémentaire au Festival?"}</StyledSubHeading>
                        <StyledTopRow>
                            <StyledLabel>
                                <StyledSubLabel>{language === "en" ? "Grand Total of Product Taken off the Fair" : "Grand total des produits pris au Festival"}</StyledSubLabel>
                                {language === "en" ? "(Minimum: $" + convertToUSD(minTotal) + ",  Maximum: $" + convertToUSD(maxTotal) + ")" : "(Minimum : " + convertToCAD(minTotal)  + " $ ,  Maximum : " + convertToCAD(maxTotal)  + " $)"}
                            </StyledLabel>
                            <TopStyledTextInput
                                value={productTakenSelected}
                                language={language}
                                onChange={handleProductTakenChange}
                            ></TopStyledTextInput>
                        </StyledTopRow>
                        <StyledRow className="bothBonuses">
                            <StyledLabel>
                                <StyledSubLabel>{language === "en" ? 'Selected Profit in Product off the \nCurrent Fair' : "Gains sélectionnés en produits au Festival"}</StyledSubLabel>
                            </StyledLabel>
                            <StyledNumber>{language === "en" ? "$" + convertToUSD(minTotal) : convertToCAD(minTotal) + " $"}</StyledNumber>
                        </StyledRow>
                        <StyledRow className="bothBonuses">
                            <StyledLabel>
                                <StyledSubLabel>{language === "en" ? 'Additional Product off the Fair from \nexisting Scholastic Dollars Balance' : "Produits additionnels pris au Festival à partir du solde de dollars Scholastic existant"}</StyledSubLabel>
                            </StyledLabel>
                            <StyledNumber>{language === "en" ? "$" + convertToUSD(remainingTotal) : convertToCAD(remainingTotal) + " $"}</StyledNumber>
                        </StyledRow>
                    </StyledCard>
                )}

                <StyledButtons>
                    <StyledWhiteButton handleClick={handleBack}>{language === "en" ? "BACK" : "Retour"}</StyledWhiteButton>
                    <StyledRedButton handleClick={handleRewards} disabled={(selected === "SPLIT" && !applied) || isAdditionalProductDisabled}>
                        {language === "en" ? "SAVE & CONTINUE" : "Sauvegarder et continuer"}
                    </StyledRedButton>
                </StyledButtons>
            </StyledDiv>
            <SaveChangesModal 
                language={language}
                saveAndPublish={putRewards}
                setModalIsOpen={setSaveChangesModalOpen}
                modalIsOpen={saveChangesModalOpen}
            />
        </>)}
    </>);
};

export default FinFormRewardsPage;

const StyledRadioButton = styled(RadioButton)`
    padding-bottom: 35px;
    align-items: normal;
`;

const ApplyButton = styled(RedButton)`
    align-self: flex-end;
    &.editAmount {
        background: #F5F5F5;
        color: #E81111;
        text-align: right;
        font-size: 11px;
        font-style: normal;
        font-weight: 600;
        line-height: 12px; /* 109.091% */
        letter-spacing: 1.5px;
        text-transform: uppercase;
        padding: 0px;
        margin-bottom: 19px;
	}
`;

const StyledTextInput = styled(CurrencyInput)`
    border-top: 1px solid #D7D7D7;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    padding: 12px 0px;
    label {
        border: none;
    }
    input {
        border: 1px solid #919191;
        max-width: 120px;
        padding: 16px;
    }
    #textInputErrorLabel {
        text-align: right;
    }
    &[data-disabled=true] {
        label#inputLabel, input:disabled {
            opacity: 1;
        }
        input:disabled {
            text-align: right;
            border: none;
            background: none;
            padding: 0;
        }
    }
    @media (max-width: 580px) {
        flex-direction: column;
        align-items: start;
        &[data-disabled=true] {
            input:disabled {
                text-align: left;
            }
        }
    }
`;

const TopStyledTextInput = styled(CurrencyInput)`
    max-width: 120px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    label {
        border: none;
    }
    input {
        border: 1px solid #919191;
        max-width: 120px;
        padding: 16px;
    }
    @media (max-width: 580px) {
        flex-direction: column;
        align-items: start;
    }
`;

const NetSales = styled.div`
    color: #333333;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-family: museo-sans;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
`;

const StyledLabel = styled.label`
    max-width: 330px;
    display: block;
	font-family: museo-sans;
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 15px;
	color: #333333;
	&.virtual {
		padding: 0px;
	}
`;

const StyledSubLabel = styled.label`
    text-wrap-mode: nowrap;
	display: block;
	font-family: museo-sans;
	font-style: normal;
	font-size: 16px;
    font-weight: 500;
    line-height: 19px;
	color: #333333;
    @media (min-width: 561px){
        white-space: pre-wrap;
    }
`;

const StyledWrappedSubLabel = styled(StyledSubLabel)`
    white-space: pre-wrap;
`;

const StyledRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-family: museo-sans;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
    gap: 15px;
    &.bothBonuses {
		border-top: 1px solid #D7D7D7;
        padding-top: 12px;
        margin-top: 12px;
	}
`;

const StyledInnerCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 35px;
`;

const StyledTopRow = styled(StyledRow)`
    padding-top: 19px;
    border-top: none;
`;

const StyledDiv = styled.div`
    text-align: right;
    max-width: 600px;
    margin: 0px auto;
    padding: 40px 40px 120px 40px;
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
    height: auto;
    @media (max-width: 1080px) {
        padding-top: 90px;
    }
`;

const StyledNumber = styled.p`
    text-wrap-mode: nowrap;
    margin: auto 0px;
`;

const StyledHeading = styled(Heading)`
    text-align: left;
    max-width: 600px;
    margin: 0px auto;
    padding: 30px 0px;
    padding-bottom: 6px;
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
`;

const SubHeading = styled(Heading)`
    text-align: left;
    font-family: museo-sans;
    font-size: 28px;
    font-weight: 300;
    line-height: 32px;
    text-align: left;
    padding-bottom: 19px;
`;

const StyledSubHeading = styled(Heading)`
    font-family: museo-sans;
    font-size: 24px;
    font-style: normal;
    font-weight: 300;
    line-height: 28px;
    text-align: left;
`;

const StyledSpacedSubHeading= styled(SubHeading)`
    margin: 0px;
    padding: 0px;
`;

const StyledSoloSubHeading = styled(StyledSubHeading)`
    margin: 0px;
    padding: 0px;
`;

const StyledCard = styled.div`
    display: flex;
    flex-direction: column;
    text-align: left;
    max-width: 600px;
    background-color: #F5F5F5;
    margin: 26px auto;
    padding: 48px 80px;
    box-sizing: border-box;
    border-radius: 16px;
    @media (max-width: 580px) {
        padding: 48px 25px;
    }
`;

const StyledButtons = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
`;

const DisclaimerText = styled.p`
    font-family: museo-sans;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 15px;
    text-align: left;
`;
